import { Component } from "@theme/component";
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  onDocumentLoaded,
} from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { DialogCloseEvent, DialogComponent } from "@theme/dialog";

/* ── Meilisearch helpers (predictive search) ──────────────────────── */

const MS_CFG = window.PredictiveSearchConfig || null;
const MS_LIMIT = (MS_CFG && MS_CFG.productLimit) || 12;
const MS_COLOR_NAMES =
  MS_CFG && Array.isArray(MS_CFG.colorNames) ? MS_CFG.colorNames : [];

function _esc(s) {
  if (!s) return "";
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

function _formatDecimal(value, decimals, thousandsSep, decimalSep) {
  const parts = value.toFixed(decimals).split(".");
  if (thousandsSep && parts[0].length > 3) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  }
  return parts.join(decimalSep || ".");
}

function _fmtPrice(amount) {
  if (!MS_CFG) return "";
  if (amount == null || isNaN(Number(amount))) return "";
  const num = Number(amount);
  const fmt = MS_CFG.moneyFormat || "{{amount}} CHF";
  let formatted;
  if (fmt.indexOf("amount_no_decimals_with_comma_separator") !== -1) {
    formatted = _formatDecimal(num, 0, ".", ",");
  } else if (fmt.indexOf("amount_with_comma_separator") !== -1) {
    formatted = _formatDecimal(num, 2, ".", ",");
  } else if (fmt.indexOf("amount_no_decimals") !== -1) {
    formatted = _formatDecimal(num, 0, ",", ".");
  } else if (fmt.indexOf("amount_with_apostrophe_separator") !== -1) {
    formatted = _formatDecimal(num, 2, "'", ".");
  } else {
    formatted = _formatDecimal(num, 2, ",", ".");
  }
  return fmt.replace(/\{\{\s*[\w_]+\s*\}\}/g, formatted);
}

function _imgSrc(img) {
  if (typeof img === "string") return img;
  return (img && (img.src || img.url)) || "";
}

function _parseImageFile(src) {
  if (!src) return null;
  const filename = src.split("/").pop().split("?")[0].toLowerCase();
  const baseName = filename.replace(/\.[^.]+$/, "");
  const parts = baseName.split("_");
  if (parts.length < 2) return null;
  // Shopify renames duplicate uploads by appending a UUID segment before the
  // extension (base_a.png -> base_a_dded9a18-....png). Strip it so the real
  // suffix (_a/_b/_1..N) is still recognised.
  if (
    parts.length > 2 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
      parts[parts.length - 1],
    )
  ) {
    parts.pop();
  }
  const suffix = parts[parts.length - 1];
  const firstChar = suffix.charAt(0);
  const firstDigit =
    firstChar >= "1" && firstChar <= "9" ? parseInt(firstChar, 10) : null;
  return {
    prefix: parts.slice(0, -1).join("_"),
    suffix,
    firstChar,
    firstDigit,
  };
}

function _findABImages(allImages, variantSku) {
  if (!Array.isArray(allImages) || allImages.length === 0) return null;

  const skuFull = (variantSku || "").toLowerCase();
  const skuBase = skuFull.split("-")[0] || "";

  let imgA = null,
    imgAPrefix = null;
  let imgAFallback = null,
    imgAFallbackPrefix = null;

  for (let i = 0; i < allImages.length; i++) {
    const src = _imgSrc(allImages[i]);
    const parsed = _parseImageFile(src);
    if (!parsed || (parsed.suffix !== "a" && parsed.firstChar !== "a"))
      continue;

    if (skuFull && parsed.prefix === skuFull) {
      imgA = src;
      imgAPrefix = parsed.prefix;
      break;
    }
    if (skuBase && parsed.prefix === skuBase && !imgA) {
      imgA = src;
      imgAPrefix = parsed.prefix;
    }
    if (!imgAFallback) {
      imgAFallback = src;
      imgAFallbackPrefix = parsed.prefix;
    }
  }

  if (!imgA && imgAFallback) {
    imgA = imgAFallback;
    imgAPrefix = imgAFallbackPrefix;
  }
  if (!imgA) return null;

  let imgB = null;
  for (let j = 0; j < allImages.length; j++) {
    const src = _imgSrc(allImages[j]);
    const parsed = _parseImageFile(src);
    if (!parsed || (parsed.suffix !== "b" && parsed.firstChar !== "b"))
      continue;
    if (parsed.prefix === imgAPrefix) {
      imgB = src;
      break;
    }
  }

  return { img1: imgA, img2: imgB || "" };
}

function _findFirstTwoByNumericOrder(allImages, variantSku) {
  if (!Array.isArray(allImages) || allImages.length === 0) return null;
  const skuFull = (variantSku || "").toLowerCase();
  const skuBase = skuFull.split("-")[0] || "";
  const withIndex = [];

  for (let i = 0; i < allImages.length; i++) {
    const src = _imgSrc(allImages[i]);
    const parsed = _parseImageFile(src);
    if (!parsed) continue;
    const prefixMatch =
      (skuFull && parsed.prefix === skuFull) ||
      (skuBase && parsed.prefix === skuBase);
    if (!prefixMatch) continue;
    const sortIndex = parsed.firstDigit != null ? parsed.firstDigit : 999;
    withIndex.push({ src, sortIndex });
  }

  withIndex.sort((a, b) => a.sortIndex - b.sortIndex);
  if (withIndex.length < 1) return null;
  return {
    img1: withIndex[0].src,
    img2: withIndex.length > 1 ? withIndex[1].src : "",
  };
}

function _resolveImages(hit) {
  const sku = (hit.variants && hit.variants[0] && hit.variants[0].sku) || "";
  const allImages = hit.images || hit.product_images || hit.media || [];

  const variantFirstSrc =
    hit.variant_images &&
    hit.variant_images.first &&
    (hit.variant_images.first.src || hit.variant_images.first.url);
  const variantSecondSrc =
    hit.variant_images &&
    hit.variant_images.second &&
    (hit.variant_images.second.src || hit.variant_images.second.url);
  const useVariantImages = variantFirstSrc && variantSecondSrc;

  const abResult = _findABImages(allImages, sku);
  const numericResult = !abResult
    ? _findFirstTwoByNumericOrder(allImages, sku)
    : null;

  let img1, img2;
  if (useVariantImages) {
    img1 = variantFirstSrc;
    img2 = variantSecondSrc;
  } else if (abResult) {
    img1 = abResult.img1;
    img2 = abResult.img2;
  } else if (numericResult) {
    img1 = numericResult.img1;
    img2 = numericResult.img2;
  } else {
    img1 = variantFirstSrc || "";
    img2 = variantSecondSrc || "";
  }

  if (img1) return { img1, img2: img2 || "" };

  for (const key of ["featured_image", "image"]) {
    if (hit[key]) {
      const src =
        typeof hit[key] === "string"
          ? hit[key]
          : hit[key].src || hit[key].url || "";
      if (src) return { img1: src, img2: "" };
    }
  }

  for (let i = 0; i < allImages.length; i++) {
    const src = _imgSrc(allImages[i]);
    if (src) return { img1: src, img2: "" };
  }

  return { img1: "", img2: "" };
}

function _resolvePrice(hit) {
  const mkt = MS_CFG && MS_CFG.market ? MS_CFG.market.handle : "ch";
  let price = null;
  let cmp = 0;

  if (hit.prices && typeof hit.prices === "object") {
    const mp = hit.prices[mkt];
    if (mp && mp.hasOwnProperty("price")) {
      price = Number(mp.price);
      cmp = Number(mp.compareAtPrice) || 0;
    } else if (hit.prices.hasOwnProperty("price")) {
      price = Number(hit.prices.price);
      cmp = Number(hit.prices.compareAtPrice) || 0;
    }
  }

  if (price === null && hit.variants && hit.variants.length > 0) {
    price = 0;
    for (const v of hit.variants) {
      const vp = Number(v.price) || 0;
      if (vp > 0 && (!price || vp < price)) price = vp;
      const vc = Number(v.compareAtPrice || v.compare_at_price) || 0;
      if (vc > cmp) cmp = vc;
    }
  }

  if (price === null) price = hit.min_price || 0;
  if (!cmp) cmp = hit.max_compare_at_price || 0;

  return { price, cmp };
}

/* --------------------------------------------------------------------
 * Default size for card links (client request 2026-09-03, refined the same
 * evening). Shopify's default variant is the first size (35), which shoppers
 * kept finding sold out on arrival. A result keeps its colour and picks:
 *   1. size 39 with real stock                      -> 39
 *   2. otherwise the smallest size with stock       -> that size
 *   3. nothing buyable in the colour, 39 exists     -> 39 ("39 - notify me")
 *   4. nothing buyable, no 39 in the range          -> Shopify's default
 * Buyability = availableForSale AND inventoryQuantity > 0, the same rule
 * blocks/buy-buttons.liquid uses. "Smallest" is numeric (a few products list
 * sizes out of order).
 * Same logic lives in
 * aico-collection-meilisearch.js, search-meilisearch.js and, for
 * Liquid-rendered cards, snippets/product-card-variant-url.liquid; the PDP
 * twin is snippets/variant-main-picker.liquid.
 * -------------------------------------------------------------------- */
const PREFERRED_SIZE = "39";

function _preferredLinkVariantId(hit, fallbackVid) {
  const variants = Array.isArray(hit.variants) ? hit.variants : [];
  let preferred = null;
  let smallestBuyable = null;
  let smallestBuyableSize = 0;
  let firstBuyable = null;
  for (const v of variants) {
    if (!v || !v.id) continue;
    const title = String(v.title);
    const buyable = !!v.availableForSale && Number(v.inventoryQuantity) > 0;
    if (title === PREFERRED_SIZE) preferred = { id: v.id, buyable };
    if (buyable) {
      if (firstBuyable === null) firstBuyable = v.id;
      const size = parseFloat(title);
      if (size > 0 && (smallestBuyable === null || size < smallestBuyableSize)) {
        smallestBuyable = v.id;
        smallestBuyableSize = size;
      }
    }
  }
  if (preferred && preferred.buyable) return preferred.id;
  if (smallestBuyable !== null) return smallestBuyable;
  if (firstBuyable !== null) return firstBuyable;
  if (preferred) return preferred.id;
  return fallbackVid;
}

function _productCardHTML(hit, idx) {
  const handle = _esc(hit.handle || hit.product_handle || "");
  const title = _esc(hit.title || "");
  const { img1, img2 } = _resolveImages(hit);
  let { price, cmp } = _resolvePrice(hit);
  // Storewide sale (display-only): override price/compare-at for products in a
  // sale collection so predictive results mirror the real Shopify automatic discount.
  if (window.StorewideSale && window.StorewideSale.enabled) {
    const sw = window.StorewideSale.apply(price, cmp, hit);
    price = sw.price;
    cmp = sw.cmp;
  }
  const priceStr = _fmtPrice(price);
  const cmpStr = cmp > 0 && cmp > price ? _fmtPrice(cmp) : "";
  const sku = _esc(
    (hit.variants && hit.variants[0] && hit.variants[0].sku) || "",
  );

  const vid =
    hit.variant_id ||
    (hit.variants && hit.variants[0] && hit.variants[0].id) ||
    "";

  if (!handle) return "";

  const localePrefix =
    (typeof Theme !== "undefined" && Theme.routes?.locale_prefix) || "";
  // Link to the preferred size of this colour - see _preferredLinkVariantId().
  const linkVid = _preferredLinkVariantId(hit, vid);
  const path = linkVid
    ? `/products/${handle}?variant=${linkVid}`
    : `/products/${handle}`;
  const href = localePrefix + path;

  let imgHtml = "";
  if (img1) {
    imgHtml = `<img src="${img1}" alt="${title}" width="100%" height="100%" loading="lazy" class="product-image-primary">`;
  } else {
    imgHtml =
      `<div class="product-image-primary product-image-placeholder" style="position:absolute;width:100%;height:100%;inset:0;background:var(--inuikii-com-light-gray,#f8f8f8);display:flex;align-items:center;justify-content:center;">` +
      `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" style="opacity:.3">` +
      `<path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"></path>` +
      `</svg></div>`;
  }
  if (img2) {
    imgHtml += `<img src="${img2}" alt="${title}" width="100%" height="100%" loading="lazy" class="product-image-secondary hidden--mobile">`;
  }

  let priceHtml = "";
  if (priceStr) {
    priceHtml = `<p class="custom-product-card-price-container">`;
    if (cmpStr) priceHtml += `<span class="compare-at-price">${cmpStr}</span>`;
    priceHtml += `<span class="price">${priceStr}</span></p>`;
  }

  const contentHtml =
    `<div class="custom-product-card-content">` +
    (title ? `<h3>${title}</h3>` : "") +
    priceHtml +
    `</div>`;

  return (
    `<div class="predictive-search-results__product-item">` +
    `<div class="custom-product-card product_index-${idx}" data-sku="${sku}">` +
    `<a href="${href}" class="custom-product-card-link">` +
    `<div class="custom-product-card-image">${imgHtml}</div>` +
    contentHtml +
    `</a></div></div>`
  );
}

function _buildProductsSectionHTML(hits) {
  if (!hits || hits.length === 0) return "";

  let cards = "";
  for (let i = 0; i < hits.length; i++) {
    cards += _productCardHTML(hits[i], i);
  }
  if (!cards) return "";

  return (
    `<style>` +
    `.predictive-search-results{height:fit-content;max-height:none;overflow-y:visible}` +
    `.predictive-search-results h3{color:var(--inuikii-com-black,#000);font-family:'ABC Diatype';font-size:12px;font-weight:700;line-height:14px;letter-spacing:.6px;text-transform:uppercase;margin-bottom:44px;width:fit-content}` +
    `.predictive-search-results__product-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:24px}` +
    `.predictive-search-results__product-item{display:flex;justify-content:center;min-width:0}` +
    `.predictive-search-results .custom-product-card{height:100%;width:100%;aspect-ratio:2/3;min-height:0;min-width:0;position:relative;display:flex;flex-direction:column;flex-grow:1;overflow:hidden}` +
    `.predictive-search-results .custom-product-card-link{display:contents;text-decoration:none;color:inherit}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-image{position:absolute;inset:0;width:100%;height:100%;overflow:hidden;background-color:var(--inuikii-com-light-gray,#f8f8f8)}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-image img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center;transition:opacity .2s ease}` +
    `.predictive-search-results .product-image-primary{opacity:1}` +
    `.predictive-search-results .product-image-secondary{opacity:0}` +
    `.predictive-search-results .custom-product-card:hover .product-image-primary{opacity:0}` +
    `.predictive-search-results .custom-product-card:hover .product-image-secondary{opacity:1}` +
    `.predictive-search-results .custom-product-card:not(:has(.product-image-secondary)):hover .product-image-primary{opacity:1}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-content{position:absolute;top:0;left:0;right:0;bottom:0;padding:0;margin:0 auto auto;width:100%;max-width:calc(100% - 20px);height:calc(100% - 20px);z-index:2;display:flex;flex-direction:column;justify-content:flex-end;gap:3px;pointer-events:none}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-content *{pointer-events:auto}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-content h3{margin:0;font-family:'ABC Diatype',sans-serif;font-size:14px;font-style:normal;font-weight:700;line-height:14px;letter-spacing:.6px;color:var(--inuikii-com-black,var(--color-black-solid,#000));text-transform:var(--title-case,uppercase)}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-price-container{margin:3px 0 0;display:flex;align-items:center;gap:5px}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-price-container .price{color:var(--inuikii-com-black,var(--color-black-solid,#000));font-family:'ABC Diatype';font-size:12px;font-style:normal;font-weight:400;line-height:14px;letter-spacing:.6px}` +
    `.predictive-search-results__product-grid .custom-product-card .custom-product-card-price-container .compare-at-price{color:var(--inuikii-com-black,var(--color-black-solid,#000));font-family:'ABC Diatype';font-size:12px;font-style:normal;font-weight:400;line-height:14px;letter-spacing:.6px;text-decoration:line-through;opacity:1}` +
    `@media screen and (min-width:750px){.predictive-search-results__product-grid{grid-template-columns:repeat(4,1fr);gap:16px}}` +
    `@media screen and (min-width:1280px){.predictive-search-results__product-grid{grid-template-columns:repeat(5,1fr);gap:24px;padding-right:132px}}` +
    `</style>` +
    `<div class="predictive-search-results__products">` +
    `<h3>Products</h3>` +
    `<div class="predictive-search-results__product-grid">${cards}</div>` +
    `</div>`
  );
}

function _detectColor(term) {
  if (!term || MS_COLOR_NAMES.length === 0) return null;
  const sorted = MS_COLOR_NAMES.slice().sort((a, b) => b.length - a.length);
  const lower = term.toLowerCase();
  for (const name of sorted) {
    const nl = name.toLowerCase();
    const idx = lower.indexOf(nl);
    if (idx === -1) continue;
    const cb = idx > 0 ? lower[idx - 1] : "";
    const ca = idx + nl.length < lower.length ? lower[idx + nl.length] : "";
    if (
      (!cb || cb === " " || cb === ",") &&
      (!ca || ca === " " || ca === ",")
    ) {
      return name;
    }
  }
  return null;
}

async function _meilisearchQuery(term, signal) {
  const color = _detectColor(term);
  const query = {
    indexUid: MS_CFG.index,
    q: term,
    limit: MS_LIMIT,
    sort: ["title:asc"],
  };
  if (color) query.filter = [`color_option = "${color}"`];

  const res = await fetch(`${MS_CFG.host}/multi-search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${MS_CFG.apiKey}`,
    },
    body: JSON.stringify({ queries: [query] }),
    signal,
  });
  if (!res.ok) throw new Error(`Meilisearch ${res.status}`);
  const data = await res.json();
  return data.results && data.results[0] ? data.results[0] : { hits: [] };
}

/* ── End Meilisearch helpers ──────────────────────────────────────── */

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ["searchInput", "predictiveSearchResults", "resetButton"];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, {
        signal,
      });
      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal },
      );

      this.addEventListener("click", this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {});
  }

  /**
   * Initialize search state when dropdown is opened
   */
  initializeSearchState() {
    this.#showInitialEmptyState();
    this.#hideContent();
    this.#hideResetButton();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        ".predictive-search-results__wrapper-queries, " +
          ".predictive-search-results__wrapper-products, " +
          ".predictive-search-results__list",
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            "predictive-search-results__wrapper-products",
          )
        ) {
          return Array.from(
            container.querySelectorAll(".predictive-search-results__card"),
          );
        }
        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card',
          ),
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) => item.getAttribute("aria-selected") === "true",
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove("keyboard-focus");
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute("aria-selected", "true");

        if (this.#isKeyboardNavigation) {
          item.classList.add("keyboard-focus");
        }
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? "instant" : "smooth",
          block: "nearest",
        });
      } else {
        item.removeAttribute("aria-selected");
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems?.length ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case "ArrowDown":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case "Tab":
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case "ArrowUp":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case "Enter": {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            "[data-single-result-url]",
          );
        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set("q", this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } =
      this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add("removing");

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Search handler - manages custom empty state and fetches results
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#showInitialEmptyState();
      this.#hideContent();
      this.#hideResetButton();
    } else {
      this.#hideInitialEmptyState();
      this.#showContent();
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
    }
  }, 200);

  /**
   * Fetch search results
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (MS_CFG) {
      return this.#getSearchResultsHybrid(searchTerm);
    }
    return this.#getSearchResultsShopify(searchTerm);
  }

  /**
   * Shopify for collections + Meilisearch /multi-search for products.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResultsHybrid(searchTerm) {
    if (this.#activeFetch) this.#activeFetch.abort();
    const ac = new AbortController();
    this.#activeFetch = ac;

    const { predictiveSearchResults } = this.refs;
    const content = this.querySelector(".predictive-search-form__content");
    const footer = this.querySelector(".predictive-search-form__footer");

    const shopifyUrl = new URL(
      Theme.routes.predictive_search_url,
      location.origin,
    );
    shopifyUrl.searchParams.set("q", searchTerm);
    shopifyUrl.searchParams.set("resources[type]", "collection");
    shopifyUrl.searchParams.set("resources[limit]", "4");

    try {
      const [shopifyResult, msResult] = await Promise.allSettled([
        this.dataset.sectionId
          ? sectionRenderer.getSectionHTML(
              this.dataset.sectionId,
              false,
              shopifyUrl,
            )
          : Promise.resolve(null),
        _meilisearchQuery(searchTerm, ac.signal),
      ]);

      if (ac.signal.aborted) return;

      // --- Parse Shopify response: collections + styles only ---
      let collectionsHTML = "";
      let shopifyStylesHTML = "";
      if (shopifyResult.status === "fulfilled" && shopifyResult.value) {
        const sectionHTML = this.#extractPredictiveSearchSectionHTML(
          shopifyResult.value,
        );
        const doc = new DOMParser().parseFromString(
          `<div>${sectionHTML}</div>`,
          "text/html",
        );
        const collectionsEl = doc.querySelector(
          ".predictive-search-results__collections",
        );
        if (collectionsEl) {
          collectionsHTML = collectionsEl.outerHTML;
        }
        doc.querySelectorAll("style").forEach((el) => {
          shopifyStylesHTML += el.outerHTML;
        });
      }

      // --- Build products HTML from Meilisearch ---
      let productsHTML = "";
      if (
        msResult.status === "fulfilled" &&
        msResult.value.hits &&
        msResult.value.hits.length > 0
      ) {
        productsHTML = _buildProductsSectionHTML(msResult.value.hits);
      }

      // --- Combine and render ---
      const hasAny = collectionsHTML || productsHTML;

      if (!hasAny) {
        const msg = this.dataset.noResultsTemplate
          ? this.dataset.noResultsTemplate.replace(/%%QUERY%%/g, searchTerm)
          : "No results found";
        predictiveSearchResults.innerHTML =
          `<div class="predictive-search-results">` +
          `<div class="predictive-search-results__no-results">` +
          `<p>${this.#escapeHtml(msg)}</p></div></div>` +
          shopifyStylesHTML;
      } else {
        predictiveSearchResults.innerHTML =
          `<div class="predictive-search-results">` +
          collectionsHTML +
          productsHTML +
          `</div>` +
          shopifyStylesHTML;
      }

      if (content instanceof HTMLElement) {
        content.style.display = "block";
        requestAnimationFrame(() => content.classList.add("show"));
      }

      if (footer instanceof HTMLElement) {
        footer.style.display = hasAny ? "block" : "none";
      }

      this.#hideInitialEmptyState();
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Hybrid search error, falling back to Shopify:", error);
      return this.#getSearchResultsShopify(searchTerm);
    } finally {
      if (this.#activeFetch === ac) this.#activeFetch = null;
    }
  }

  /**
   * Fetch search results from Shopify Section Rendering API (fallback)
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResultsShopify(searchTerm) {
    if (!this.dataset.sectionId) {
      return;
    }

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");

    const { predictiveSearchResults } = this.refs;

    try {
      const rawMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        url,
      );

      if (rawMarkup) {
        const resultsMarkup =
          this.#extractPredictiveSearchSectionHTML(rawMarkup);
        morph(predictiveSearchResults, resultsMarkup);

        const content = this.querySelector(".predictive-search-form__content");
        const footer = this.querySelector(".predictive-search-form__footer");

        const hasActualResults =
          content &&
          content.querySelector(".predictive-search-results__no-results") ===
            null &&
          content.children.length > 0;

        const hasNoResultsMessage =
          content &&
          content.querySelector(".predictive-search-results__no-results") !==
            null;

        if (content instanceof HTMLElement) {
          if (hasActualResults || hasNoResultsMessage) {
            content.style.display = "block";
            requestAnimationFrame(() => {
              content.classList.add("show");
            });
          } else if (
            content.children.length === 0 &&
            this.dataset.noResultsTemplate
          ) {
            const message = this.dataset.noResultsTemplate.replace(
              /%%QUERY%%/g,
              searchTerm,
            );
            content.innerHTML = `<div class="predictive-search-results__no-results"><p>${this.#escapeHtml(message)}</p></div>`;
            content.style.display = "block";
            requestAnimationFrame(() => {
              content.classList.add("show");
            });
          } else {
            content.style.display = "none";
            content.classList.remove("show");
          }
        }

        if (footer instanceof HTMLElement) {
          footer.style.display = hasActualResults ? "block" : "none";
        }

        this.#hideInitialEmptyState();
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #hideInitialEmptyState() {
    // CSS handles the transition via .mobile-search-menu.has-input class
  }

  #showInitialEmptyState() {
    // CSS handles the transition via .mobile-search-menu.has-input class removal
  }

  #showContent() {
    const content = this.querySelector(".predictive-search-form__content");
    if (content instanceof HTMLElement) {
      content.style.display = "block";
    }

    const searchDropdown = document.getElementById("search-dropdown");
    if (searchDropdown) {
      searchDropdown.classList.add("has-input");
    }

    const mobileSearchMenu = document.getElementById("mobile-search-menu");
    if (mobileSearchMenu) {
      mobileSearchMenu.classList.add("has-input");
    }
  }

  #hideContent() {
    const content = this.querySelector(".predictive-search-form__content");
    if (content instanceof HTMLElement) {
      content.style.display = "none";
      content.classList.remove("show");
    }

    const footer = this.querySelector(".predictive-search-form__footer");
    if (footer instanceof HTMLElement) {
      footer.style.display = "none";
    }

    const searchDropdown = document.getElementById("search-dropdown");
    if (searchDropdown) {
      searchDropdown.classList.remove("has-input");
    }

    const mobileSearchMenu = document.getElementById("mobile-search-menu");
    if (mobileSearchMenu) {
      mobileSearchMenu.classList.remove("has-input");
    }
  }

  /**
   * Escape HTML special characters for safe insertion into DOM
   * @param {string} text - Raw text
   * @returns {string} Escaped HTML string
   */
  #escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Extract the predictive search section content from the API response.
   * The API may return a full HTML document; we need the section's inner HTML.
   * @param {string} html - Raw response HTML
   * @returns {string} HTML fragment to inject into the results container
   */
  #extractPredictiveSearchSectionHTML(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const section =
      doc.querySelector("#shopify-section-predictive-search") ||
      doc.querySelector('[id^="shopify-section-"][id*="predictive-search"]');
    if (section && section.innerHTML) {
      return section.innerHTML;
    }
    return html;
  }

  #resetSearch = async () => {
    const { searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = "";
    this.#hideResetButton();
    this.#showInitialEmptyState();
    this.#hideContent();
  };
}

if (!customElements.get("predictive-search-component")) {
  customElements.define(
    "predictive-search-component",
    PredictiveSearchComponent,
  );
}
